import * as React from "react"
import { Check, ChevronsUpDown, Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

/**
 * Searchable Combobox built on shadcn Command + Popover.
 *
 * Props:
 *  - value: string (current value)
 *  - onValueChange: (val: string) => void
 *  - options: Array<{ value: string, label: string, group?: string, disabled?: boolean }>
 *  - placeholder?: string
 *  - searchPlaceholder?: string
 *  - emptyText?: string
 *  - disabled?: boolean
 *  - className?: string
 *  - data-testid?: string
 *  - allowCreate?: boolean  — show "Create X" option when no match
 *  - onCreate?: (name: string) => void
 */
export const Combobox = React.forwardRef(({
  value,
  onValueChange,
  options = [],
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  disabled = false,
  className,
  allowCreate = false,
  onCreate,
  "data-testid": testId,
}, ref) => {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")

  // Deduplicate options by value
  const dedupedOptions = React.useMemo(() => {
    const seen = new Set()
    return options.filter(o => {
      if (!o.value || seen.has(o.value)) return false
      seen.add(o.value)
      return true
    })
  }, [options])

  const selectedLabel = dedupedOptions.find(o => o.value === value)?.label || ""

  const handleSelect = (val) => {
    if (val === "__create__") {
      if (onCreate && search.trim()) {
        onCreate(search.trim())
        setSearch("")
      }
    } else {
      onValueChange(val === value ? "" : val)
    }
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full justify-between font-normal h-9 text-sm",
            !value && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate">{selectedLabel || placeholder}</span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={true}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {allowCreate && search.trim() ? (
                <button
                  type="button"
                  className="w-full text-left px-2 py-1.5 text-sm hover:bg-accent rounded cursor-pointer"
                  onMouseDown={(e) => { e.preventDefault(); handleSelect("__create__"); }}
                >
                  Create "{search.trim()}"
                </button>
              ) : (
                emptyText
              )}
            </CommandEmpty>
            <CommandGroup>
              {dedupedOptions.map(option => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  disabled={option.disabled}
                  onSelect={() => handleSelect(option.value)}
                >
                  <Check className={cn("mr-2 h-3.5 w-3.5", value === option.value ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
})

Combobox.displayName = "Combobox"
